import {email, sameAs} from 'vuelidate/lib/validators';
import brazilianPhone from 'commons/js/validators/brazilianPhone';
import cpf from 'commons/js/validators/cpf';
import cnpj from 'commons/js/validators/cnpj';
import {errorMessages} from 'commons/js/components/forms/messages/inputValidations';
import {nonDigit, groupByNth} from 'commons/js/RegExp';


async function recalculate(shipment) {
  const treatedVolumes = shipment.package ? [
    shipment.package
  ] : shipment.volumes.map((volume) => {
    return {
      width: volume.width,
      height: volume.height,
      length: volume.length,
      weight: volume.weight,
      qntd: volume.qntd,
    }
  });

  treatedVolumes.map((volume) => {
    volume.qntd = volume.qntd ? volume.qntd : 1;
    return volume;
  });

  const params = {
    from: shipment.from,
    to: shipment.to,
    insurance_value: shipment.insuranceValue,
    receipt: shipment.receipt,
    own_hand: shipment.own_hand,
    reverse: shipment.reverse,
    services: shipment.serviceId,
    hash: shipment.hash,
    volumes: JSON.stringify(treatedVolumes)
  };

  let key = 'ME_shipping_recalculate:' + JSON.stringify(params);

  const cachedShipment = getShipmentFromLocalStorage(key);

  if (cachedShipment) {
    return cachedShipment;
  }

  let query = formatParamsForRequest(params);

  let {data} = await axios.get(`/api/v2/calculator?${query}`);

  const filteredData = ("id" in data) ? data : data.find((service) => service.id === shipment.serviceId);

  saveShipmentInLocalStorage(key, filteredData);

  return filteredData;
}

function formatParamsForRequest(params) {
  return Object.keys(params)
    .filter((key) => params[key])
    .map((key) => key + '=' + params[key])
    .join('&');
}

function getShipmentFromLocalStorage(key) {
  if (typeof(Storage) !== 'undefined') {
    const value = localStorage.getItem(key);
    const ONE_DAY_IN_MILLISECONDS = 86400000;
    const THIRTY_SECONDS_IN_MILLISECONDS = 30000;

    if (value) {
      const obj = JSON.parse(value);
      let ttl = ONE_DAY_IN_MILLISECONDS;

      if (obj.data.error || !obj.data.price) {
        ttl = THIRTY_SECONDS_IN_MILLISECONDS;
      }

      if ((new Date() - obj.timestamp) <= ttl) {
        return obj.data;
      }
    }
  }

  return false;
}

function saveShipmentInLocalStorage(key, shipment) {
  if (!shipment || shipment.error) {
    return;
  }

  typeof(Storage) !== 'undefined' && localStorage.setItem(key, JSON.stringify({
    timestamp: new Date().getTime(),
    data: shipment,
  }));
}

function currencyFilter(value, currency = 'R$', hasZero = true) {
  value = String(value);
  let matches = value.match(/(\,|\.)(\d{2})$/);
  if (matches && matches.length >= 3) {
    value = value.substr(0, value.length - 3).replace(nonDigit(), '') + '.' + matches[2];
  }
  value = parseFloat(value);
  if (!(value && !isNaN(value) && isFinite(value))) value = 0;
  if (!hasZero && !value) return '-';
  return currency + ' ' + Number(value).toLocaleString('pt-BR', {
    currency: 'BRL',
    minimumFractionDigits: 2
  });
}

function isNfKeyAvulsa(nfKey) {
  const nfSerie = Number(nfKey.substr(22, 3));

  return nfSerie >= 890 && nfSerie < 900;
}

function validateNfKeyCnpj(person) {
  const nfCnpj = this.shipment.nfKey.substr(6, 14);
  this.setWarningCompanyDocument(false);
  if (!this.$v.shipment.nfKey.$invalid) {
    if (this.shipment[person].documentType === 'CNPJ') {
      if (this.shipment[person].document === '') {
        setDocument.call(this, person, nfCnpj);
        return;
      }
      if (this.shipment[person].document !== nfCnpj) {
        this.setWarningCompanyDocument(true);
        return;
      }
    }
  }
}

function validateNfKeyParanaModel() {
  let nfModel = this.shipment.nfKey.substr(20, 2);

  if (this.shipment.reverse) {
    if (this.shipment.sender.stateAbbr === 'PR') {
      if (nfModel !== '55') {
        this.setWarningParanaModel(true);
        return;
      }
    }
  }

  if (this.shipment.recipient.stateAbbr === 'PR') {
    if (nfModel !== '55') {
      this.setWarningParanaModel(true);
      return;
    }
  }

  this.setWarningParanaModel(false);
}

function validateDeclarationFields(refs) {
  let allFieldsValid = true;

  if(!refs) return allFieldsValid;

  refs.forEach(ref => {
    if(!ref.validate()) {
      allFieldsValid = false;
    }
  });

  return allFieldsValid;
}

function setWarningParanaModel(state) {
  this.setShipmentData({
    shipmentId: this.shipmentId,
    target: 'warningParanaModel',
    keys: [],
    value: state,
  });
}

function extractNfFromKey(nfKey) {
  return nfKey.substr(25, 9);
}

function extractNfModelFromKey(nfKey) {
  return nfKey.substr(20, 2);
}

function setDocument(person, document) {
  this.setShipmentData({
    shipmentId: this.shipmentId,
    target: person,
    keys: ['document'],
    value: document,
  });
}

function setPreviousDocument(person, document) {
  this.setShipmentData({
    shipmentId: this.shipmentId,
    target: person,
    keys: ['previousDocument'],
    value: document,
  });
}

function setDocumentType(person, type) {
  this.setShipmentData({
    shipmentId: this.shipmentId,
    target: person,
    keys: ['documentType'],
    value: type,
  });
}

// O nome das refs sempre possuem o padrao '{ID da transportadora}-{target}-{contador do form}'
function createRefString(target) {
  return `${this.shipment.company.id}-${target}-${this.shipment.formNumber}`;
}

function scrollToID(target) {
  this.$scrollTo(`[id="${target}"]`);
}

function maskNfKey(nfKey) {
  return nfKey.replace(nonDigit(), '').match(groupByNth(4)).join(' ');
}


function setDocumentByType(person) {
  const isDeclaration = this.shipment.isDeclaration;
  const declarationTypes = this.formStructure.declaration && this.formStructure.declaration[person].document;

  if (isDeclaration && !declarationTypes) {
    return false;
  }

  const documentTypes = isDeclaration ? declarationTypes : this.formStructure[person].document;
  const hasOnlyOneDocumentType = !documentTypes.CPF || !documentTypes.CNPJ;

  if (!hasOnlyOneDocumentType) {
    return false;
  }

  const documentType = documentTypes.CPF ? 'CPF' : 'CNPJ';
  const documentValue = documentTypes.CPF ? this.shipment.me.document : this.shipment.settingDocument;
  const isLoggedUser = (this.shipment.reverse && person === 'recipient') || (!this.shipment.reverse && person === 'sender');

  this.setDocumentType(person, documentType);
  isLoggedUser && this.setDocument(person, documentValue);

  return true;
}

function setDocumentFromDefaultSetting() {
  const person = this.shipment.reverse ? 'recipient' : 'sender';
  const {
    documentType,
    document,
    previousDocument,
  } = this.shipment[person];

  if (this.shipment.isDeclaration) {
    if (this.shipment.loadedLegalDocumentsFromSetting) {
      const newPreviousDocument = documentType === 'CPF' ? previousDocument : document;
      this.setDocumentType(person, 'CPF');
      this.setPreviousDocument(person, newPreviousDocument);
      this.setDocument(person, this.shipment.me.document);

      return;
    }
  }

  let newDocument = documentType === 'CNPJ' ? document : previousDocument;
  const newPreviousDocument = documentType === 'CNPJ' ? previousDocument : document;

  if (this.shipment.loadedLegalDocumentsFromSetting) {
    newDocument = this.shipment.settingDocument || newDocument;
  }

  this.setDocumentType(person, 'CNPJ');
  this.setPreviousDocument(person, newPreviousDocument);
  this.setDocument(person, newDocument);
}

export {
  recalculate,
  currencyFilter,
  isNfKeyAvulsa,
  validateNfKeyCnpj,
  validateNfKeyParanaModel,
  extractNfFromKey,
  extractNfModelFromKey,
  setWarningParanaModel,
  setDocument,
  setPreviousDocument,
  setDocumentType,
  createRefString,
  scrollToID,
  maskNfKey,
  setDocumentByType,
  setDocumentFromDefaultSetting,
  validateDeclarationFields
};

export default {
  data() {
    return {
      CORREIOS_ID: 1,
      LATAM_ID: 6,
    }
  },
  methods: {
    setDocument,
    setPreviousDocument,
    setDocumentType,
    createRefString,
    scrollToID,
    setDocumentByType,
    setDocumentFromDefaultSetting,
    validateDeclarationFields,

    setCanChangeDocument(person, state) {
      this.setShipmentData({
        shipmentId: this.shipmentId,
        target: [person],
        keys: ['canChangeDocument'],
        value: state,
      });
    },

    companyCheckoutSpecificValidations() {
      return true;
    },

    createPersonValidation(person) {
      let validators = {};

      for (const [prop, value] of Object.entries(this.validator[person])) {
        validators[prop] = {};

        if (value.required) {
          validators[prop] = {
            required(value) {
              this[person][prop].error = !!value ? '' : errorMessages.empty;

              return !!value;
            }
          };
        }
      }

      if (validators.document) {
        const secondPerson = person === 'sender' ? 'recipient' : 'sender';
        const translatedSecondPerson = secondPerson === 'sender' ? 'remetente' : 'destinatário';
        const documentValidator = {
          CPF: cpf,
          CNPJ: cnpj,
        };

        validators.document.validDocument = function(document) {
          const isValidDocument = documentValidator[this.shipment[person].documentType](document);

          if (!isValidDocument) {
            this[person].document.error = isValidDocument ? '' : errorMessages.format;
            return false;
          }

          if (!validators.document.required) {
            return true;
          }

          const isSameDocumentType = this.shipment[person].documentType === this.shipment[secondPerson].documentType;
          const isSameDocument = document === this.shipment[secondPerson].document;
          const hasDocuments = !!document && !!this.shipment[secondPerson].document;

          if (isSameDocumentType && isSameDocument && hasDocuments) {
            this[person].document.error = errorMessages.document.same(translatedSecondPerson);

            return false;
          }

          return isValidDocument;
        };
      }

      if (validators.name) {
        validators.name.validName = function(name) {
          const minCharacter = 1;
          const isInvalid = name.length <= minCharacter;

          if (isInvalid) {
            this[person].name.error = errorMessages.characters;
          }

          return !isInvalid;
        }
      }

      if (validators.phone) {
        validators.phone.validPhone = function(phone) {
          const isValidPhone = brazilianPhone(phone);

          if (isValidPhone) {
            return true;
          }

          if (phone.length) {
            this[person].phone.error = errorMessages.format;
          }

          return false;
        }
      }

      if (validators.email) {
        validators.email.validEmail = function(emailValue) {
          const isValidEmail = email(emailValue);

          if (isValidEmail) {
            return true;
          }

          if (emailValue.length) {
            this[person].email.error = errorMessages.format;
          }

          return false;
        }
      }

      if (validators.ie && validators.ie.required) {
        validators.ie.requiredIf = function(ie){
          if (this.shipment[person].documentType !== 'CNPJ') {
            return true;
          }

          return !!ie;
        };
      }

      if (validators.economicActivityCode && validators.economicActivityCode.required) {
        validators.economicActivityCode.requiredIf = function(economicActivityCode) {
          if (this.shipment[person].documentType !== 'CNPJ') {
            return true;
          }

          const treatedCodeLength = economicActivityCode.replace(nonDigit(),'').length;

          if (treatedCodeLength === 7) {
            return true;
          }

          if (treatedCodeLength > 0) {
            this[person].economicActivityCode.error = errorMessages.format;
          }

          return false;
        },

          validators.economicActivityCode.required = function(economicActivityCode) {
            if (this.shipment[person].documentType !== 'CNPJ') {
              return true;
            }

            this[person].economicActivityCode.error = errorMessages.empty;

            return !!economicActivityCode;
          }
      }

      return validators;
    },

    createValidations() {
      let validators = {
        shipment: {
          recipient: this.createPersonValidation('recipient'),
          reminder: {},
          sender: this.createPersonValidation('sender'),
        },
      };

      if (this.validator.reminder) {
        if (this.validator.reminder.required) {
          validators.shipment.reminder = {
            required(reminder) {
              if (reminder) {
                return true;
              }

              this.reminder.error = errorMessages.empty;

              return false;
            }
          };
        }
      }

      validators.acceptTermsAndConditions = {
        isChecked: sameAs(() => true),
      };

      validators.acceptTermsCentralizedAndConditions= {
        isChecked: sameAs(() => true),
      };

      validators.acceptTermsAndConditionsLoggi= {
        isChecked: sameAs(() => true),
      };

      validators.acceptTermsAndConditionsLoggiCollect = {
        isChecked: sameAs(() => true),
      };

      return validators;
    },

    changeDocumentType(person) {
      const newType = this.shipment[person].documentType === 'CPF' ? 'CNPJ' : 'CPF';

      this.setShipmentData({
        shipmentId: this.shipmentId,
        target: person,
        keys: ['documentType'],
        value: newType,
      });

      const currentDocument = this.shipment[person].document;
      this.setDocument(person, this.shipment[person].previousDocument);
      this.setPreviousDocument(person, currentDocument);
    },

    decimalValue(value) {
      value = String(value);
      let matches = value.match(/(\,|\.)(\d{2})$/);
      if (matches && matches.length >= 3) {
        value = value.substr(0, value.length - 3).replace(nonDigit(), '') + '.' + matches[2];
      }
      return parseFloat(value);
    },

    filterDeclarationItems() {
      const products = this.shipment.declarationItems;
      const filteredProducts = products.map((product) => {
        return {
          name: product.name,
          quantity: product.quantity,
          unitary_value: this.decimalValue(product.unitary_value),
        }
      });

      return filteredProducts;
    },

    validateDeclarationItems() {
      if (!this.shipment.declarationItems.length) {
        return true;
      }

      const IS_CORREIOS = this.shipment.company.id === 1;
      const typeSelector = this.$refs[this.createRefString('type-selector')];
      const contentDeclaration = typeSelector.$refs[this.createRefString('content-declaration')];
      const declarationItemsRefs = contentDeclaration.$refs['declaration-item'];
      const maxInsuranceValue = this.shipment.service.details.restrictions.insurance_value.max;
      const isDeclarationValid = this.validateDeclarationFields(declarationItemsRefs);

      if(!isDeclarationValid) {
        throw {invalidField: 'type-details'};
      }

      if(!IS_CORREIOS) {
        const isValidTotalPrice = contentDeclaration.totalPrice <= maxInsuranceValue;

        if (!isValidTotalPrice) {
          throw {invalidField: 'type-details'};
        }
      }
    },

    saveLegalDocuments() {
      const person = this.shipment.reverse ? 'recipient' : 'sender';

      if (this.shipment[person].documentType !== 'CNPJ') {
        return;
      }

      if (this.shouldSaveLegalDocuments) {
        const treatedDocument = this.shipment[person].document.replace(nonDigit(), '');
        const treatedIE = this.shipment[person].ie.replace(nonDigit(), '');

        treatedDocument && axios.put(`/api/v2/me/settings/company-document/${treatedDocument}`);
        treatedIE && axios.put(`/api/v2/me/settings/state-register/${treatedIE}`);

        return;
      }

      axios.delete('/api/v2/me/settings/company-document');
      axios.delete('/api/v2/me/settings/state-register');
    },

    touchAllFormFields() {
      this.$v.$touch();

      const shipmentDocumentsRef = this.$refs[this.createRefString('type-selector')].$v;
      shipmentDocumentsRef && shipmentDocumentsRef.$touch();

      const agencySelector = this.$refs[this.createRefString('agency-selector')];
      agencySelector && agencySelector.$v.slAgency.$touch();
    },

    validateInputs() {
      if (this.shipment.isDeclaration) {
        this.validateDeclarationItems();
      }

      if (!this.shipment.isDeclaration) {
        this.validateShipmentDocuments();
      }

      this.validateCollectAgency();

      if (this.$v.shipment.$invalid) {
        for (const fieldname in this.$v.shipment.$params) {
          if (this.$v.shipment[fieldname].$invalid) {
            throw {invalidField: fieldname};
          }
        }
      }

      if (!this.acceptTermsAndConditions) {
        throw {invalidField: 'accept-terms-checkbox'};
      }

      if (this.isIndustrialService(this.shipment.service.id) && !this.acceptTermsCentralizedAndConditions) {
        throw {invalidField: 'accept-terms-checkbox-centralized'};
      }

      if (this.isIndustrialService(this.shipment.service.id) && !this.acceptTermsAndConditionsLoggi) {
        throw {invalidField: 'accept-terms-checkbox-loggi'};
      }

      if (this.isLoggiCollect(this.shipment.service.id) && !this.acceptTermsAndConditionsLoggiCollect) {
        throw {invalidField: 'accept-terms-checkbox-loggi-collect'};
      }
    },

    validateShipmentDocuments() {
      const shipmentDocumentsRef = this.$refs[this.createRefString('type-selector')].$v;
      if (shipmentDocumentsRef.$invalid) {
        throw {invalidField: 'type-details'};
      }
    },

    validateCollectAgency() {
      if (!this.formStructure.shipping.agencySelector || this.isLoggiCollect(this.shipment.service.id)) {
        return;
      }
      if (!this.validator.agency || !this.validator.agency.required) {
        return;
      }
      if (this.shipment.collect.agency.id && this.shipment.collect.agency.name) {
        return;
      }

      throw {invalidField: 'shipping'};
    },

    createPersonData(person) {
      const personDocumentType = this.shipment[person].documentType;
      let personDocumentKey = personDocumentType === 'CNPJ' ? 'company_document' : 'document';
      let personDocumentValue = String(this.shipment[person].document).replace(nonDigit(), '');

      let personData = {
        name: String(this.shipment[person].name || '').trim(),
        phone: String(this.shipment[person].phone || '').replace(nonDigit(), ''),
        postal_code: String(this.shipment[person].postalCode || '').replace(nonDigit(), ''),
        address: String(this.shipment[person].street || '').trim(),
        number: String(this.shipment[person].houseNumber || '').trim(),
        complement: String(this.shipment[person].complement || '').trim(),
        district: String(this.shipment[person].district || '').trim(),
        city: String(this.shipment[person].city || '').trim(),
        state_abbr: String(this.shipment[person].stateAbbr || '').trim(),
        [personDocumentKey]: personDocumentValue,
        know_my_cnpj: personDocumentType === 'CNPJ',
        state_register: personDocumentType === 'CNPJ' ? String(this.shipment[person].ie) : '',
        economic_activity_code: String(this.shipment[person].economicActivityCode || '').replace(nonDigit(), ''),
      }

      if (person === 'recipient') {
        personData.email = String(this.shipment.recipient.email);
        personData.note = String(this.shipment.recipient.observations);
      }

      return personData;
    },

    createCartItem() {
      const sender = this.createPersonData('sender');
      const recipient = this.createPersonData('recipient');

      let cartItem = {
        from: sender,
        to: recipient,
        package: {
          width: Number(String(this.shipment.package.width).replace(',', '.')),
          height: Number(String(this.shipment.package.height).replace(',', '.')),
          length: Number(String(this.shipment.package.length).replace(',', '.')),
          weight: Number(String(this.shipment.package.weight).replace(',', '.'))
        },
        products: [],
        options: {
          non_commercial: this.shipment.isDeclaration,
          reverse: this.shipment.reverse,
          insurance_value: this.decimalValue(this.shipment.insuranceValue),
          receipt: !!Number(this.shipment.receipt),
          own_hand: !!Number(this.shipment.ownHand),
          collect: !!Number(this.shipment.collect.value),
          reminder: String(this.shipment.reminder || '').trim(),
        },
        service: this.shipment.service.id
      };

      if (this.shipment.isDeclaration) {
        cartItem.products = this.filterDeclarationItems();
      }

      if (!this.shipment.isDeclaration) {
        cartItem.options.invoice = {
          number: String(this.shipment.nf).replace(nonDigit(), ''),
          key: String(this.shipment.nfKey).replace(nonDigit(), ''),
        };

        if (this.shipment.xml) {
          cartItem.options.invoice.xml_content = this.shipment.xml;
        }
      }

      if (this.formStructure.shipping.agencySelector && !this.isLoggiCollect(this.shipment.service.id)) {
        cartItem.agency = Number(this.shipment.collect.agency.id || 0);
      }

      if (this.shipment.volumes.isVolumes) {
        cartItem.volumes = this.shipment.volumes.items;
      }

      return cartItem;
    },

    validateForm() {
      this.touchAllFormFields();
      this.validateInputs();
      this.companyCheckoutSpecificValidations();
    },

    addToCart(buyOneClick) {
      try {
        this.validateForm();

        const cartItem = this.createCartItem();

        this.loading = true;

        this.saveLegalDocuments();

        this.requestToCart(buyOneClick, cartItem);

      } catch(error) {
        if (error.invalidField) {
          this.scrollToID(this.createRefString(error.invalidField));
        }
      }
    },

    async requestToCart(isOneClick, cartItem) {
      const headers = {
        'ME-BUY-ORIGIN': this.shipment.buyOrigin,
        'ME-BUY-TYPE': this.shipment.buyType,
        'ME-BUY-NOW': isOneClick,
        'ME-BUY-SPREADSHEETS': this.shipment.buySpreadSheets
      };

      try {
        const {data} = await axios.post('/api/v2/me/cart', cartItem, {headers});

        // Garante que o BE crie um novo carrinho ativo para o usuário
        await axios.get('/api/v2/user-cart');
        // Salva a referência das etiquetas selecionadas no BD
        await axios.post('/api/v2/user-cart', {orders: [data.id]});

        window.$me_nav('increment-cart');

        this.setShipmentData({
          shipmentId: this.shipmentId,
          target: 'addedToCart',
          keys: [],
          value: true
        });

        if (isOneClick) {
          let form = document.createElement('form');
          form.method = 'GET';
          form.action = MEConfig.get('dash.url') + '/checkout';

          let token = document.createElement('input');
          token.type = 'hidden';
          token.name = '_token';
          token.value = MEConfig.get('csrf-token');

          form.appendChild(token);

          let input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'orders[]';
          input.value = data.id;

          form.appendChild(input);

          document.body.appendChild(form);
          form.submit();
        }

      } catch(error) {
        const {response: {data}} = error;
        if (data.errors) {
          let errors = '';
          Object.values(data.errors).map(el => {
            errors += el[0] + '<br>';
          });
          this.modalAlertContent = errors;
        }
        if (data.error) {
          this.modalAlertContent = data.error;
        }
        if (!this.modalAlertContent) {
          this.modalAlertContent = 'Não foi possível adicionar o pedido ao carrinho';
        }
        this.isModalAlertActive = true;
      } finally {
        this.loading = false;
      }
    },
  }
}
